import { gql } from '@apollo/client'
import { Caption, Chip, Col, ColNumber, Container, Heading, Row } from '@farol-ds/react'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'

import { PlanFrequencyLabel } from '../../../../constants'
import { useRegisterLockEvent, useSendFrequencyToggleEvent } from '../../../../hooks'
import { LockEventName } from '../../../../types'
import { getCardsByFrequency } from '../../../../utils'
import { FrequencyToggle } from '../../../frequency-toggle'
import { PaywallCarousel } from '../../../paywall-carousel'
import { OfferCard } from '../offer-card'
import {
  DynamicMultipleOffersSwitchFrequencyLockContent_OfferLockDataFragment,
  DynamicMultipleOffersSwitchFrequencyLockContent_ViewerFragment,
} from './__generated__/dynamic-multiple-offers-switch-frequency-lock-content.graphql'
import styles from './dynamic-multiple-offers-switch-frequency-lock-content.module.scss'

export interface DynamicMultipleOffersSwitchFrequencyLockContentProps {
  offerLockData: DynamicMultipleOffersSwitchFrequencyLockContent_OfferLockDataFragment
  viewer: DynamicMultipleOffersSwitchFrequencyLockContent_ViewerFragment | null
  isPaywall?: boolean
}

export const DynamicMultipleOffersSwitchFrequencyLockContent = ({
  offerLockData,
  viewer,
  isPaywall = false,
}: DynamicMultipleOffersSwitchFrequencyLockContentProps) => {
  const { header, body } = offerLockData || {}

  const { toggle, title } = header || {}
  const { options, defaultOption, description } = toggle || {}
  const { text, benefit } = description || {}
  const hasToggle = !!options.length

  const { cards } = body || {}

  const { registerLockEvent } = useRegisterLockEvent()
  const { sendFrequencyToggleEvent } = useSendFrequencyToggleEvent()
  const [currentFrequency, setCurrentFrequency] = useState(defaultOption || options[0])

  const cardsForFrequency = useMemo(() => {
    if (!currentFrequency) return [...cards]

    const { monthlyCards, yearlyCards } = getCardsByFrequency(cards)

    return currentFrequency === PlanFrequencyLabel.MONTHLY ? monthlyCards : yearlyCards
  }, [cards, currentFrequency])

  const handleChangeFrequency = (frequency: string) => {
    setCurrentFrequency(frequency)

    const lockEventProperties = cardsForFrequency?.[0]?.lockEventProperties
    if (!lockEventProperties) return

    const { experiment_id, feature, current_plan, modal_type, from_component, pid } =
      lockEventProperties

    sendFrequencyToggleEvent({
      frequency,
      experiment_id,
      feature,
      current_plan,
      modal_type,
      from_component,
      pid,
    })
  }

  useEffect(() => {
    cardsForFrequency.forEach((card) =>
      registerLockEvent({
        eventName: LockEventName.LOCK_VIEW,
        eventProperties: [card.lockEventProperties],
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardsForFrequency])

  return (
    <Container
      type={isPaywall ? 'default' : 'fluid'}
      className={classNames(styles.container, {
        [styles.singlePaywall]: isPaywall && cardsForFrequency.length < 3,
      })}
    >
      {isPaywall && (
        <Row className={styles.paywallHeader}>
          <Col xs={12}>
            <Heading size="lg">{title}</Heading>
          </Col>
        </Row>
      )}

      {hasToggle && (
        <Row>
          <Col xs={12} className={styles.toggle}>
            <FrequencyToggle
              options={options}
              currentFrequency={currentFrequency}
              onChangeFrequency={handleChangeFrequency}
            />
            {(text || benefit) && (
              <Caption>
                {text ? `${text} ` : ''}
                {benefit && (
                  <Chip kind="secondary" size="sm">
                    <span dangerouslySetInnerHTML={{ __html: benefit }} />
                  </Chip>
                )}
              </Caption>
            )}
          </Col>
        </Row>
      )}

      {isPaywall && cardsForFrequency.length > 2 ? (
        <PaywallCarousel
          cards={cardsForFrequency}
          renderCard={(card) => <OfferCard offerCardData={card} viewer={viewer} />}
          mobileClassName={styles.mobilePaywallOffersContent}
          carouselItemClassName={styles.carouselItem}
        />
      ) : (
        <Row className={classNames({ [styles.singleOffer]: cardsForFrequency.length < 2 })}>
          {cardsForFrequency.map((card, index) => (
            <Col
              key={`${card.title}-${index}`}
              xs={12}
              md={(12 / cardsForFrequency.length) as ColNumber}
              className={styles.offerCard}
            >
              <OfferCard offerCardData={card} viewer={viewer} />
            </Col>
          ))}
        </Row>
      )}
    </Container>
  )
}

DynamicMultipleOffersSwitchFrequencyLockContent.fragments = {
  offerLockData: gql`
    fragment DynamicMultipleOffersSwitchFrequencyLockContent_offerLockData on OfferLockData {
      header {
        title
        toggle {
          options
          defaultOption
          description {
            text
            benefit
          }
        }
      }
      body {
        cards {
          ...OfferCard_offerCardData
        }
      }
    }
    ${OfferCard.fragments.offerCardData}
  `,
  viewer: gql`
    fragment DynamicMultipleOffersSwitchFrequencyLockContent_viewer on Profile {
      ...OfferCard_viewer
    }
    ${OfferCard.fragments.viewer}
  `,
}
